import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { ViewProduct } from '../../../../pages/admin-dashboard/products/admin-products.model';
import { Router } from '@angular/router';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { toProductListTableRows } from './admin-list-view.mapper';
import { ProductService } from '../../../../services/product/product.service';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { finalize } from 'rxjs';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

@Component({
  selector: 'app-admin-list-view',
  imports: [Tables, ConfirmationDialog, SuccessfulNotification],
  templateUrl: './admin-list-view.html',
  styleUrl: './admin-list-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminListView {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private productService = inject(ProductService);

  hasSubproducts = input<boolean | ''>('');

  paginatedProducts = input.required<ViewProduct[]>();
  isFilterApplied = input(false);

  openDeleteDialoug = output<{ product: ViewProduct }>();

  confirmationModal = signal<boolean>(false);
  type = signal<'deactivate' | 'activate' | null>(null);
  isSuccessNotification = signal<boolean>(false);
  loadingActivation = signal<boolean>(false);
  errorActivation = signal<string>('');
  productIdToActivate = signal<number | null>(null);

  deleteConfirmationModal = signal<boolean>(false);
  productIdToDelete = signal<number | null>(null);
  loadingDelete = signal<boolean>(false);
  errorDelete = signal<string>('');
  deleteSuccess = signal<boolean>(false);

  activeProductIds = computed(
    () =>
      new Set(
        this.paginatedProducts()
          .filter((p) => p.isActive)
          .map((p) => p.id),
      ),
  );

  localActiveState = signal<Map<number, boolean>>(new Map());

  isProductActive(productId: number): boolean {
    const local = this.localActiveState();
    if (local.has(productId)) return local.get(productId)!;
    return this.activeProductIds().has(productId);
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  isEnglish() {
    return this.locale.startsWith('en');
  }

  rows = computed(() =>
    toProductListTableRows(
      this.paginatedProducts(),
      this.isArabic(),
      this.hasSubproducts(),
      (id) => this.isProductActive(id),
    ),
  );

  headerItems = [
    { key: 'title', label: $localize`:@@productNameSearch:Product Name` },
    { key: 'mfgCompany', label: $localize`:@@adminMFGCompany:MFG Company` },
    { key: 'customerPrice', label: $localize`:@@peoplePrice:People Price` },
    {
      key: 'sellerPrice',
      label: `${$localize`:@@sellerPrice:Seller Price /`} ${$localize`:@@sellerDiscount:Seller Discount`}`,
      type: 'priceDiscount',
    },
    {
      key: 'businessPrice',
      label: `${$localize`:@@pharmacyPriceTableHeader:Pharmacy Price /`} ${$localize`:@@pharmacyDiscount:Pharmacy Discount`}`,
      type: 'priceDiscount',
    },
    { key: 'quantity', label: $localize`:@@totalAvailableQty:Available/ Total Quantity` },
    { key: 'createdOnBy', label: $localize`:@@adminListPharmaciesCreatedOnFrom:Created On / Created By` },
    {
      key: 'lastModifiedOnBy',
      label: $localize`:@@adminListPharmaciesCreatedOnFromLastUpdated:Last Modified On / Modified By`,
    },
    { key: 'actions', label: '', type: 'actions' },
  ];

  readonly ACTIONS = {
    view: { id: 1, titleEn: 'View Product Details', titleAr: 'عرض تفاصيل الصنف' },
    edit: { id: 2, titleEn: 'Edit Product', titleAr: 'تعديل الصنف' },
    delete: { id: 3, titleEn: 'Delete Product', titleAr: 'حذف الصنف' },
    // Tables shows id 4 for active rows and id 5 for inactive ones (via row.activationStatus).
    deactivate: { id: 4, titleEn: 'Mark as inactive', titleAr: 'تعيين كغير نشط' },
    activate: { id: 5, titleEn: 'Mark as active', titleAr: 'تعيين كنشط' },
  } satisfies Record<string, ActionInterface>;

  currentActions: ActionInterface[] = Object.values(this.ACTIONS);

  handleAction(e: { event: Event; actionId: number; entity: { id: number } }) {
    e.event?.stopPropagation();
    const id = e.entity.id;
    switch (e.actionId) {
      case this.ACTIONS.view.id:
        this.router.navigate(['/admin/products/view-product/', id]);
        break;
      case this.ACTIONS.edit.id:
        this.router.navigate(['/admin/products/edit-product/', id]);
        break;
      case this.ACTIONS.delete.id:
        this.onDeleteProduct(id);
        break;
      case this.ACTIONS.deactivate.id:
        this.onActiveConfirmationModal(true, id);
        break;
      case this.ACTIONS.activate.id:
        this.onActiveConfirmationModal(false, id);
        break;
    }
  }

  onActiveConfirmationModal(isActive: boolean, productId: number) {
    this.confirmationModal.set(true);
    this.type.set(isActive ? 'deactivate' : 'activate');
    this.productIdToActivate.set(productId);
  }

  onCancelConfirmationModal() {
    this.confirmationModal.set(false);
    this.type.set(null);
  }

  onDeleteProduct(productId: number) {
    this.productIdToDelete.set(productId);
    this.errorDelete.set('');
    this.deleteConfirmationModal.set(true);
  }

  confirmDelete() {
    const productId = this.productIdToDelete();
    if (!productId) return;
    this.loadingDelete.set(true);
    this.productService
      .deleteProduct(productId)
      .pipe(finalize(() => this.loadingDelete.set(false)))
      .subscribe({
        next: () => {
          this.deleteConfirmationModal.set(false);
          this.deleteSuccess.set(true);
          this.openDeleteDialoug.emit({
            product: this.paginatedProducts().find((p) => p.id === productId)!,
          });
          setTimeout(() => this.deleteSuccess.set(false), 2000);
        },
        error: (err) => {
          const errorBody = err.error || err;
          const msgAr = errorBody?.messageAr || errorBody?.error?.messageAr;
          const msgEn = errorBody?.messageEn || errorBody?.error?.messageEn;
          this.errorDelete.set(
            this.isArabic()
              ? msgAr || 'لا يمكن حذف هذا المنتج حالياً'
              : msgEn || 'Cannot delete this product right now',
          );
        },
      });
  }

  deactivateProduct() {
    const productId = this.productIdToActivate();
    if (!productId) return;
    this.loadingActivation.set(true);
    this.productService
      .deactivateProduct(productId)
      .pipe(finalize(() => this.loadingActivation.set(false)))
      .subscribe({
        next: () => {
          this.localActiveState.update((map) => new Map(map).set(productId, false));
          this.confirmationModal.set(false);
          this.isSuccessNotification.set(true);
          setTimeout(() => {
            this.onCancelConfirmationModal();
            this.isSuccessNotification.set(false);
          }, 2000);
        },
        error: (error) => {
          this.errorActivation.set(
            this.isArabic()
              ? error.messageAr || error.messageEn || 'حدث خطأ أثناء إلغاء تفعيل الصنف. الرجاء المحاولة مرة أخرى.'
              : error.messageEn ||
                  error.messageAr ||
                  'An error occurred while deactivating the product. Please try again.',
          );
        },
      });
  }

  activateProduct() {
    const productId = this.productIdToActivate();
    if (!productId) return;
    this.loadingActivation.set(true);
    this.productService
      .activateProduct(productId)
      .pipe(finalize(() => this.loadingActivation.set(false)))
      .subscribe({
        next: () => {
          this.localActiveState.update((map) => new Map(map).set(productId, true));
          this.confirmationModal.set(false);
          this.isSuccessNotification.set(true);
          setTimeout(() => {
            this.onCancelConfirmationModal();
            this.isSuccessNotification.set(false);
          }, 2000);
        },
        error: (error) => {
          this.errorActivation.set(
            this.isArabic()
              ? error.messageAr || error.messageEn || 'حدث خطأ أثناء تفعيل الصنف. الرجاء المحاولة مرة أخرى.'
              : error.messageEn ||
                  error.messageAr ||
                  'An error occurred while activating the product. Please try again.',
          );
        },
      });
  }

  confirmationModalContent = computed(() => {
    const ar = this.isArabic();
    const type = this.type();
    const loading = this.loadingActivation();

    const content = {
      deactivate: {
        title: ar ? 'تأكيد إلغاء التفعيل' : 'Confirm Deactivation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في إلغاء تفعيل هذا الصنف؟'
          : 'Are you sure you want to deactivate this product?',
        confirmContent: ar
          ? loading
            ? 'جارٍ إلغاء التفعيل...'
            : 'تأكيد إلغاء التفعيل'
          : loading
            ? 'Deactivating...'
            : 'Confirm Deactivation',
      },
      activate: {
        title: ar ? 'تأكيد تفعيل الصنف' : 'Confirm Product Activation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في تفعيل هذا الصنف؟ سيصبح الصنف مرئياً للعملاء ومتاحاً للشراء'
          : 'Are you sure you want to activate this product? The product will become visible to customers and available for purchase.',
        confirmContent: ar
          ? loading
            ? 'جارٍ التفعيل...'
            : 'تأكيد التفعيل'
          : loading
            ? 'Activating...'
            : 'Confirm Activation',
      },
    };

    return content[type!];
  });
}
